import { useTranslation } from '@silospot/i18n/react';
import type { CspCheckResult, GtmContainerOption, GtmInstallPlan } from '@silospot/shared';
import { toast } from '@silospot/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Cloud, Code2, Copy, Loader2, ShieldCheck, TriangleAlert } from 'lucide-react';
import { type ReactElement, useEffect, useState } from 'react';
import { TRACK_URL, api, serverError } from '../../lib/api';
import { Badge } from './kit';

type MethodKey = 'gtm' | 'cloudflare' | 'manual';

/**
 * Tracker install — the choice screen + 3 methods (A GTM · B Cloudflare/Zaraz · C
 * manual). Reused at onboarding step 2 AND in Settings → Integrations. The choice
 * cards are gated by the super-admin feature flags (a method OFF disappears). All
 * three converge on the SAME "first signal" verification.
 */
export function TrackerInstall({
  projectId,
  projectDomain,
  publicKey,
  secretKey,
  onDone,
  embedded,
  onboarding,
  onFinishLater,
}: {
  projectId: string;
  projectDomain: string;
  publicKey?: string;
  secretKey?: string;
  onDone: () => void;
  embedded?: boolean;
  /** Onboarding wizard mode: pick a method by card + "Next" (no click-to-enter),
   *  and hide the manual method (kept available in Settings). */
  onboarding?: boolean;
  /** Anti-dead-end (onboarding): if the first signal never arrives, let the user
   *  reach the dashboard with the tracker left pending — never trapped on verify. */
  onFinishLater?: () => void;
}): ReactElement {
  const { t } = useTranslation('pages');
  const [view, setView] = useState<'choice' | MethodKey>('choice');
  // Reveal the install method cards even when a tracker is already installed
  // (e.g. to switch method) — the "connected" summary shows by default then.
  const [forceCards, setForceCards] = useState(false);
  // Onboarding: the card selected before pressing "Next".
  const [selected, setSelected] = useState<MethodKey | null>(null);

  const methodsQ = useQuery({ queryKey: ['tracker-methods'], queryFn: () => api.trackerMethods() });
  const cfQ = useQuery({
    queryKey: ['cf-status', projectId],
    queryFn: () => api.cfStatus(projectId),
  });
  const gtmQ = useQuery({ queryKey: ['gtm-status'], queryFn: () => api.gtmStatus() });
  // Real stack detection — fetches the site once and reports cloudflare / gtm.
  const stackQ = useQuery({
    queryKey: ['detect-stack', projectId],
    queryFn: () => api.detectStack(projectId),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  // Is a tracker already in place? Cloudflare/Zaraz injected, or a GTM container
  // installed. Drives the "connected" summary instead of the install cards.
  const zarazInstQ = useQuery({
    queryKey: ['cf-zaraz', projectId],
    queryFn: () => api.zarazStatus(projectId),
    enabled: !!cfQ.data?.connected,
    retry: false,
  });
  const gtmInstallQ = useQuery({
    queryKey: ['gtm-install', projectId],
    queryFn: () => api.gtmInstallStatus(projectId),
    retry: false,
  });
  const installedMethod: MethodKey | null =
    cfQ.data?.connected && zarazInstQ.data?.injected
      ? 'cloudflare'
      : gtmInstallQ.data?.installed
        ? 'gtm'
        : null;

  // Resume the right method after an OAuth round-trip (GTM or Cloudflare).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gtm') === 'connected') {
      setView('gtm');
      void gtmQ.refetch();
      return;
    }
    const cf = params.get('cloudflare');
    if (!cf) return;
    setView('cloudflare');
    if (cf === 'oauth_connected') {
      toast.success(t('ti.zReturnConnected'));
      void cfQ.refetch();
    } else if (cf === 'no_refresh') {
      toast.error(t('ti.zReturnNoRefresh'));
    } else if (cf === 'expired') {
      toast.info(t('ti.zReturnExpired'));
    } else {
      toast.error(t('ti.zReturnError'));
    }
    // Clean the query param so a refresh doesn't replay the toast.
    params.delete('cloudflare');
    const qs = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
  }, [gtmQ, cfQ, t]);

  const flags = methodsQ.data;
  // What the live probe actually found on the site (null = nothing / unreachable).
  const detected: MethodKey | null = stackQ.data?.cloudflare
    ? 'cloudflare'
    : stackQ.data?.gtm
      ? 'gtm'
      : null;
  // Recommendation: already connected (Cloudflare > GTM) wins; otherwise the real
  // site detection (Cloudflare > GTM); otherwise manual.
  const recommended: MethodKey = cfQ.data?.connected
    ? 'cloudflare'
    : gtmQ.data?.connected
      ? 'gtm'
      : (detected ?? 'manual');

  const CARDS = (
    [
      { key: 'gtm', enabled: !!flags?.gtm, tag: t('ti.tagOneClick'), time: '~1 min' },
      { key: 'cloudflare', enabled: !!flags?.cloudflare, tag: t('ti.tagNoCode'), time: '~4 min' },
      { key: 'manual', enabled: true, tag: t('ti.tagUniversal'), time: '~2 min' },
    ] as Array<{ key: MethodKey; enabled: boolean; tag: string; time: string }>
  ).filter((m) => m.enabled);

  if (view === 'gtm')
    return (
      <MethodGtm
        projectId={projectId}
        projectDomain={projectDomain}
        onBack={() => setView('choice')}
        onFallback={() => setView('manual')}
        onDone={onDone}
        onFinishLater={onFinishLater}
        onboarding={onboarding}
      />
    );
  if (view === 'cloudflare')
    return (
      <MethodZaraz
        projectId={projectId}
        projectDomain={projectDomain}
        onBack={() => setView('choice')}
        onFallback={() => setView('manual')}
        onDone={onDone}
        onFinishLater={onFinishLater}
        onboarding={onboarding}
      />
    );
  if (view === 'manual')
    return (
      <MethodManual
        projectId={projectId}
        publicKey={publicKey}
        secretKey={secretKey}
        onBack={() => setView('choice')}
        onDone={onDone}
        onFinishLater={onFinishLater}
      />
    );

  // Already installed → show a "connected" summary (not the 3 install cards),
  // distinguishing "installed, awaiting first visit" from an active signal.
  if (view === 'choice' && installedMethod && !forceCards)
    return (
      <InstalledSummary
        projectId={projectId}
        method={installedMethod}
        onReconfigure={() => setForceCards(true)}
        onDone={onDone}
      />
    );

  return (
    <div>
      {!embedded ? <div className={kicker}>{t('ti.stepKicker')}</div> : null}
      <h1 className={h1}>
        {t('ti.choiceTitle')} <span className="text-primary">{projectDomain}</span>
      </h1>
      <p className={lead}>{t('ti.choiceLead')}</p>

      {detected && flags?.[detected] ? (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-clean-border bg-clean-bg px-3.5 py-2.5 text-[12.5px] text-clean-fg">
          <Check className="h-4 w-4 shrink-0" />
          {t(detected === 'cloudflare' ? 'ti.detectCf' : 'ti.detectGtm')}
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-3">
        {CARDS.map((m) => {
          const rec = m.key === recommended;
          const active = onboarding ? selected === m.key : rec;
          const Icon = m.key === 'gtm' ? ShieldCheck : m.key === 'cloudflare' ? Cloud : Code2;
          return (
            <button
              type="button"
              key={m.key}
              onClick={() => (onboarding ? setSelected(m.key) : setView(m.key))}
              className={`relative flex flex-col rounded-2xl border p-4 text-left transition hover:border-primary ${active ? 'border-primary bg-accent-bg' : 'border-border'}`}
            >
              {rec ? (
                <span className="absolute right-3 top-3 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold uppercase text-white">
                  {t('ti.recommended')}
                </span>
              ) : null}
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-bg text-primary">
                <Icon className="h-5 w-5" />
              </span>
              <div className="mt-2.5 text-[15px] font-bold text-text">{t(`ti.m_${m.key}`)}</div>
              <div className="mt-1.5 flex gap-1.5">
                <Badge tone="neutral">{m.tag}</Badge>
                <Badge tone="blue">{m.time}</Badge>
              </div>
              <div className="mt-2.5 text-[12px] leading-relaxed text-text-2">
                <span className="font-semibold text-text">{t('ti.forWho')}</span>{' '}
                {t(`ti.who_${m.key}`)}
              </div>
              <div className="mt-1.5 text-[12px] leading-relaxed text-text-3">
                {t(`ti.desc_${m.key}`)}
              </div>
              {onboarding ? (
                active ? (
                  <span className="mt-3 inline-flex items-center gap-1 text-[12.5px] font-semibold text-primary">
                    <Check className="h-3.5 w-3.5" /> {t('ti.selected')}
                  </span>
                ) : null
              ) : (
                <span className="mt-3 text-[12.5px] font-semibold text-primary">
                  {t('ti.chooseCta')} →
                </span>
              )}
            </button>
          );
        })}
      </div>

      {onboarding ? (
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            disabled={!selected}
            onClick={() => selected && setView(selected)}
            className={btnPrimary}
          >
            {t('ti.next')}
          </button>
        </div>
      ) : (
        <div className="mt-4 flex items-center justify-between">
          <span className="text-[12px] text-text-3">{t('ti.otherMethods')}</span>
        </div>
      )}
    </div>
  );
}

// --- Method A: Google Tag Manager ------------------------------------------
function MethodGtm({
  projectId,
  projectDomain,
  onBack,
  onFallback,
  onDone,
  onFinishLater,
  onboarding,
}: {
  projectId: string;
  projectDomain: string;
  onBack: () => void;
  onFallback: () => void;
  onDone: () => void;
  onFinishLater?: () => void;
  onboarding?: boolean;
}): ReactElement {
  const { t } = useTranslation('pages');
  const qc = useQueryClient();
  const [phase, setPhase] = useState<
    'intro' | 'manage' | 'container' | 'confirm' | 'verify' | 'error'
  >('intro');
  const [err, setErr] = useState<string | null>(null);
  const [container, setContainer] = useState<GtmContainerOption | null>(null);
  const [plan, setPlan] = useState<GtmInstallPlan | null>(null);
  const [confirmUninstall, setConfirmUninstall] = useState(false);

  const statusQ = useQuery({ queryKey: ['gtm-status'], queryFn: () => api.gtmStatus() });
  const installQ = useQuery({
    queryKey: ['gtm-install', projectId],
    queryFn: () => api.gtmInstallStatus(projectId),
  });
  const connected = !!statusQ.data?.connected;
  const installed = !!installQ.data?.installed;
  // From the intro, route to the right landing state: an existing install → the
  // management panel; merely connected (e.g. post-OAuth) → container choice. Wait
  // for the install status to settle first — it often resolves after the lighter
  // connect status, and routing early would flash the re-install flow.
  useEffect(() => {
    if (phase !== 'intro' || installQ.isLoading) return;
    if (installed) setPhase('manage');
    else if (connected) setPhase('container');
  }, [connected, installed, phase, installQ.isLoading]);

  const uninstall = useMutation({
    mutationFn: () => api.gtmUninstall(projectId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gtm-install', projectId] });
      toast.success(t('ti.gtmUninstalled'));
      setConfirmUninstall(false);
      setPhase('container');
    },
    onError: (e) => {
      setErr((e as { message?: string }).message ?? 'unknown');
      setPhase('error');
    },
  });
  const disconnect = useMutation({
    mutationFn: () => api.gtmDisconnect(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gtm-status'] });
      void qc.invalidateQueries({ queryKey: ['gtm-install', projectId] });
      toast.info(t('ti.gtmDisconnectedMsg'));
      setPhase('intro');
    },
    onError: (e) => toast.error(serverError(e) ?? t('ti.errGeneric')),
  });

  const connect = useMutation({
    // In the wizard, return INTO the wizard on THIS site (not the bare route under
    // the modal, which could be another site's dashboard); ?wiz reopens it.
    mutationFn: () =>
      api.gtmConnect(
        onboarding ? `/projects/${projectId}?wiz=${projectId}` : window.location.pathname,
      ),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (e) => toast.error(serverError(e) ?? t('ti.errGeneric')),
  });
  const containersQ = useQuery({
    queryKey: ['gtm-containers', projectId],
    queryFn: () => api.gtmContainers(projectId),
    enabled: connected && phase === 'container',
    retry: false,
  });
  useEffect(() => {
    if (containersQ.isError) {
      setErr('nocontainer');
      setPhase('error');
    }
    if (containersQ.data && !container) {
      setContainer(
        containersQ.data.containers.find((c) => c.match) ?? containersQ.data.containers[0] ?? null,
      );
    }
  }, [containersQ.isError, containersQ.data, container]);

  const dryRun = useMutation({
    mutationFn: () =>
      api.gtmInstall(projectId, {
        accountId: container?.accountId ?? '',
        containerId: container?.containerId ?? '',
      }),
    onSuccess: (r) => {
      setPlan(r.plan ?? null);
      setPhase('confirm');
    },
  });
  const install = useMutation({
    mutationFn: () =>
      api.gtmInstall(projectId, {
        accountId: container?.accountId ?? '',
        containerId: container?.containerId ?? '',
        confirm: true,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gtm-install', projectId] });
      // Tag installed (tracker posted) → chain to Google Ads in the wizard;
      // verify the live signal later on the dashboard. Settings keeps the check.
      if (onboarding) onDone();
      else setPhase('verify');
    },
    onError: (e) => {
      setErr((e as { message?: string }).message ?? 'unknown');
      setPhase('error');
    },
  });

  if (phase === 'error' && err)
    return (
      <MethodError
        title={t(`ti.gtmErr_${err}_t`)}
        body={t(`ti.gtmErr_${err}_b`, { site: projectDomain })}
        onRetry={() => {
          setErr(null);
          setPhase(connected ? 'container' : 'intro');
        }}
        onFallback={onFallback}
        fallbackLabel={t('ti.useManual')}
      />
    );

  if (phase === 'verify')
    return (
      <VerifySignal
        projectId={projectId}
        site={projectDomain}
        onDone={onDone}
        onFinishLater={onFinishLater}
      />
    );

  if (phase === 'manage') {
    const st = installQ.data;
    return (
      <div>
        <MethodHead
          icon={<ShieldCheck className="h-5 w-5" />}
          title={t('ti.gtmManageTitle')}
          sub={t('ti.gtmManageSub')}
          onBack={onBack}
        />
        <Reassure>{t('ti.gtmManageOk', { site: projectDomain })}</Reassure>
        <div className="mt-4 flex flex-col gap-2 rounded-xl border border-border bg-bg/40 p-3.5">
          <Row
            label={t('ti.gtmContainer')}
            value={`${st?.containerName ?? '—'} · ${st?.publicId ?? ''}`}
          />
          <Row label={t('ti.gtmVersion')} value={st?.versionId ? `#${st.versionId}` : '—'} />
          <Row label={t('ti.gtmTag')} value="« Silospot tracker »" />
        </div>
        {confirmUninstall ? (
          <div className="mt-3 rounded-xl border border-[#f3c9c9] bg-fraud-bg px-3.5 py-3">
            <div className="text-[13px] leading-relaxed text-fraud-fg">
              {t('ti.gtmUninstallAsk')}
            </div>
            <div className="mt-3 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setConfirmUninstall(false)} className={btnGhost}>
                {t('ti.back')}
              </button>
              <button
                type="button"
                disabled={uninstall.isPending}
                onClick={() => uninstall.mutate()}
                className={btnDanger}
              >
                {uninstall.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t('ti.gtmUninstall')}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-5 flex items-center justify-between">
            <button
              type="button"
              disabled={disconnect.isPending}
              onClick={() => disconnect.mutate()}
              className={btnGhost}
            >
              {disconnect.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t('ti.gtmDisconnect')}
            </button>
            <button type="button" onClick={() => setConfirmUninstall(true)} className={btnDanger}>
              {t('ti.gtmUninstall')}
            </button>
          </div>
        )}
      </div>
    );
  }

  if (phase === 'intro')
    return (
      <div>
        <MethodHead
          icon={<ShieldCheck className="h-5 w-5" />}
          title={t('ti.m_gtm')}
          sub={t('ti.gtmSub')}
        />
        <Reassure>{t('ti.gtmReassure')}</Reassure>
        <div className="my-5 flex justify-center">
          <button
            type="button"
            disabled={connect.isPending}
            onClick={() => connect.mutate()}
            className={btnGoogle}
          >
            {connect.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ShieldCheck className="h-4 w-4" />
            )}
            {t('ti.gtmConnect')}
          </button>
        </div>
        <BackRow onBack={onBack} />
      </div>
    );

  if (phase === 'container')
    return (
      <div>
        <MethodHead
          icon={<ShieldCheck className="h-5 w-5" />}
          title={t('ti.gtmPickTitle')}
          sub={t('ti.gtmPickSub')}
          onBack={onBack}
        />
        {containersQ.isLoading ? (
          <div className="h-20 animate-pulse rounded-xl bg-neutral-bg" />
        ) : (
          <div className="flex flex-col gap-1.5">
            {containersQ.data?.containers.map((c) => (
              <button
                type="button"
                key={c.containerId}
                onClick={() => setContainer(c)}
                className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left ${container?.containerId === c.containerId ? 'border-primary bg-accent-bg' : 'border-border hover:bg-bg'}`}
              >
                <ShieldCheck className="h-5 w-5 text-primary" />
                <div className="flex-1">
                  <div className="text-[13.5px] font-semibold text-text">{c.name}</div>
                  <div className="font-mono text-[11.5px] text-text-3">{c.publicId}</div>
                </div>
                {c.match ? <Badge tone="clean">{t('ti.matchesSite')}</Badge> : null}
              </button>
            ))}
          </div>
        )}
        <div className="mt-5 flex items-center justify-between">
          <button type="button" onClick={onBack} className={btnGhost}>
            {t('ti.back')}
          </button>
          <button
            type="button"
            disabled={!container || dryRun.isPending}
            onClick={() => dryRun.mutate()}
            className={btnPrimary}
          >
            {dryRun.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t('ti.continue')}
          </button>
        </div>
      </div>
    );

  // confirm
  return (
    <div>
      <MethodHead
        icon={<ShieldCheck className="h-5 w-5" />}
        title={t('ti.gtmConfirmTitle')}
        sub={t('ti.gtmConfirmSub')}
        onBack={() => setPhase('container')}
      />
      <div className="flex flex-col gap-2 rounded-xl border border-border bg-bg/40 p-3.5">
        {[
          [t('ti.gtmStep1t'), `« ${plan?.tagName ?? 'Silospot tracker'} »`],
          [t('ti.gtmStep2t'), `« ${plan?.trigger ?? 'All Pages'} »`],
          [t('ti.gtmStep3t'), t('ti.gtmStep3b')],
        ].map(([a, b], i) => (
          <div key={a} className="flex items-start gap-2.5">
            <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary text-[11px] font-semibold text-white">
              {i + 1}
            </span>
            <div className="text-[13px]">
              <strong className="text-text">{a}</strong>
              <span className="text-text-3"> — {b}</span>
            </div>
          </div>
        ))}
      </div>
      <Reassure className="mt-3">{t('ti.gtmReversible')}</Reassure>
      <div className="mt-5 flex items-center justify-between">
        <button type="button" onClick={() => setPhase('container')} className={btnGhost}>
          {t('ti.back')}
        </button>
        <button
          type="button"
          disabled={install.isPending}
          onClick={() => install.mutate()}
          className={btnPrimary}
        >
          {install.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {t('ti.gtmCreatePublish')}
        </button>
      </div>
    </div>
  );
}

// --- Method B: Cloudflare / Zaraz ------------------------------------------
function MethodZaraz({
  projectId,
  projectDomain,
  onBack,
  onFallback,
  onDone,
  onFinishLater,
  onboarding,
}: {
  projectId: string;
  projectDomain: string;
  onBack: () => void;
  onFallback: () => void;
  onDone: () => void;
  onFinishLater?: () => void;
  onboarding?: boolean;
}): ReactElement {
  const { t } = useTranslation('pages');
  const [phase, setPhase] = useState<'loading' | 'toggle' | 'prereq' | 'verify' | 'error'>(
    'loading',
  );
  const [err, setErr] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const zarazQ = useQuery({
    queryKey: ['cf-zaraz', projectId],
    queryFn: () => api.zarazStatus(projectId),
    retry: false,
  });
  useEffect(() => {
    // Initial routing only; never yank the user out of the inline connect/verify
    // sub-flow on a background refetch.
    if (phase !== 'loading') return;
    const z = zarazQ.data;
    if (!z) return;
    if (!z.connected) setPhase('prereq');
    else if (!z.proxied) {
      setErr('noproxy');
      setPhase('error');
    } else if (!z.hasPerm) {
      setErr('noperm');
      setPhase('error');
    } else if (z.injected) {
      // Tracker posted (Zaraz injected). In the wizard, chain straight to the
      // Google Ads step — the first-beacon verification is non-blocking and shown
      // later on the dashboard. Outside onboarding, go to the live signal check.
      if (onboarding) onDone();
      else setPhase('verify');
    } else setPhase('toggle');
  }, [zarazQ.data, phase, onboarding, onDone]);

  const activate = useMutation({
    mutationFn: () => api.zarazActivate(projectId),
    // Posted → continue to Google Ads in the wizard; verify live elsewhere.
    onSuccess: () => (onboarding ? onDone() : setPhase('verify')),
    onError: (e) => {
      const m = (e as { message?: string }).message ?? 'unknown';
      const known = m === 'noproxy' || m === 'noperm' || m === 'quota' || m === 'disabled';
      setErr(known ? m : 'unknown');
      setErrMsg(known ? null : (serverError(e) ?? null));
      setPhase('error');
    },
  });

  // 1-click OAuth: navigate the browser to the Cloudflare consent screen. On
  // return (?cloudflare=oauth_connected), TrackerInstall re-opens this method and
  // the status refetch lands the user on the inline "activate injection" step.
  const connect = useMutation({
    // In the wizard, return INTO the wizard on THIS site (?wiz), not the bare route
    // under the modal (which is often another site's dashboard).
    mutationFn: () =>
      api.cfOAuthUrl(
        projectId,
        onboarding ? `/projects/${projectId}?wiz=${projectId}` : window.location.pathname,
      ),
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (e) => {
      setErr('unknown');
      setErrMsg(serverError(e) ?? null);
      setPhase('error');
    },
  });

  if (phase === 'error' && err)
    return (
      <MethodError
        title={t(`ti.zErr_${err}_t`)}
        body={errMsg ?? t(`ti.zErr_${err}_b`, { site: projectDomain })}
        onRetry={() => {
          setErr(null);
          setErrMsg(null);
          void zarazQ.refetch();
          setPhase('loading');
        }}
        onFallback={onFallback}
        fallbackLabel={t('ti.useManual')}
      />
    );
  if (phase === 'verify')
    return (
      <VerifySignal
        projectId={projectId}
        site={projectDomain}
        bonus
        onDone={onDone}
        onFinishLater={onFinishLater}
      />
    );
  if (phase === 'loading') return <div className="h-24 animate-pulse rounded-xl bg-neutral-bg" />;

  if (phase === 'toggle')
    return (
      <div>
        <MethodHead
          icon={<Cloud className="h-5 w-5 text-[#f6821f]" />}
          title={t('ti.m_cloudflare')}
          sub={t('ti.zAlready')}
          onBack={onBack}
        />
        <Reassure>
          {t('ti.zToggleReassure', { zone: zarazQ.data?.injected ? projectDomain : projectDomain })}
        </Reassure>
        <div className="mt-4 flex items-center justify-between rounded-xl border border-border bg-bg/40 p-3.5">
          <div>
            <div className="text-[13.5px] font-semibold text-text">{t('ti.zToggleTitle')}</div>
            <div className="text-[12px] text-text-3">{t('ti.zToggleSub')}</div>
          </div>
          <button
            type="button"
            disabled={activate.isPending}
            onClick={() => activate.mutate()}
            className={btnPrimary}
          >
            {activate.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t('ti.zActivate')}
          </button>
        </div>
        <BackRow onBack={onBack} />
      </div>
    );

  // prereq (Cloudflare not connected yet)
  return (
    <div>
      <MethodHead
        icon={<Cloud className="h-5 w-5 text-[#f6821f]" />}
        title={t('ti.m_cloudflare')}
        sub={t('ti.zPrereqSub')}
        onBack={onBack}
      />
      <div className="flex items-start gap-2.5 rounded-xl border border-[#f3dca6] bg-[#fdf6e8] px-3.5 py-3 text-[12.5px] text-[#7c5212]">
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <div>{t('ti.zPrereq', { site: projectDomain })}</div>
      </div>
      <p className="mt-3 text-[12.5px] text-text-2">{t('ti.zInjectionPerms')}</p>
      <div className="mt-5 flex items-center justify-between">
        <button type="button" onClick={onBack} className={btnGhost}>
          {t('ti.back')}
        </button>
        <button
          type="button"
          disabled={connect.isPending}
          onClick={() => connect.mutate()}
          className={btnPrimary}
        >
          {connect.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {t('ti.zConnectCf')}
        </button>
      </div>
    </div>
  );
}

// --- Method C: manual snippet ----------------------------------------------
function MethodManual({
  projectId,
  publicKey,
  secretKey,
  onBack,
  onDone,
  onFinishLater,
}: {
  projectId: string;
  publicKey?: string;
  secretKey?: string;
  onBack: () => void;
  onDone: () => void;
  onFinishLater?: () => void;
}): ReactElement {
  const { t } = useTranslation('pages');
  const [verifying, setVerifying] = useState(false);
  const projectQ = useQuery({
    queryKey: ['project-keys', projectId],
    queryFn: () => api.getProject(projectId),
    enabled: !publicKey || !secretKey,
  });
  const pk = publicKey ?? projectQ.data?.project.publicKey ?? '';
  const sk = secretKey ?? projectQ.data?.project.secretKey ?? '';
  const snippet = pk
    ? `<script defer src="${TRACK_URL}/t.js" data-key="${pk}" data-sign="${sk}"></script>`
    : '';

  if (verifying)
    return (
      <VerifySignal
        projectId={projectId}
        site={t('ti.yourSite')}
        onDone={onDone}
        onFinishLater={onFinishLater}
      />
    );
  return (
    <div>
      <MethodHead
        icon={<Code2 className="h-5 w-5" />}
        title={t('ti.m_manual')}
        sub={t('ti.manualSub')}
        onBack={onBack}
      />
      <p className={`${lead} mt-1`}>{t('ti.manualLead')}</p>
      <div className="rounded-lg bg-[#0c0d10] p-3">
        <pre className="whitespace-pre-wrap break-all font-mono text-[11.5px] leading-relaxed text-[#c8cbd2]">
          <code>{snippet}</code>
        </pre>
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard.writeText(snippet);
            toast.success(t('onb.copy'));
          }}
          className="mt-2.5 inline-flex items-center gap-1.5 rounded-md bg-[#1c1f26] px-2.5 py-1.5 text-[12px] font-semibold text-[#c8cbd2] hover:bg-[#262a33]"
        >
          <Copy className="h-3.5 w-3.5" /> {t('onb.copy')}
        </button>
      </div>
      <div className="mt-5 flex items-center justify-between">
        <button type="button" onClick={onBack} className={btnGhost}>
          {t('ti.back')}
        </button>
        <button type="button" onClick={() => setVerifying(true)} className={btnPrimary}>
          {t('ti.verifyInstall')}
        </button>
      </div>
    </div>
  );
}

// --- Shared ----------------------------------------------------------------
/** Settings summary shown when a tracker is already installed: which method, and
 *  whether the first signal has arrived yet (auto-polled, no manual refresh). */
function InstalledSummary({
  projectId,
  method,
  onReconfigure,
  onDone,
}: {
  projectId: string;
  method: MethodKey;
  onReconfigure: () => void;
  onDone: () => void;
}): ReactElement {
  const { t } = useTranslation('pages');
  const verify = useQuery({
    queryKey: ['verify-signal', projectId],
    queryFn: () => api.verifyProject(projectId),
    refetchInterval: (q) => (q.state.data?.verified ? false : 8000),
  });
  const active = !!verify.data?.verified;

  // Connection verification (our own headless test → a marked, EXCLUDED beacon),
  // distinct from the first REAL visitor (`active`). Auto-triggered once.
  const conn = useQuery({
    queryKey: ['verify-conn', projectId],
    queryFn: () => api.verifyConnection(projectId),
    refetchInterval: (q) => (q.state.data?.verified ? false : 4000),
  });
  const connVerified = !!conn.data?.verified;
  const startVerify = useMutation({ mutationFn: () => api.startVerifyConnection(projectId) });
  // Attempt counter (0 = not triggered yet) drives the auto-trigger AND a timeout:
  // a headless visit completes well under 45s, so if the flag has not flipped by
  // then the visit failed (CSP/unreachable) — show a clear retry, never an endless
  // spinner.
  const [attempt, setAttempt] = useState(0);
  const [connTimedOut, setConnTimedOut] = useState(false);
  useEffect(() => {
    if (conn.data && !conn.data.verified && attempt === 0) {
      setAttempt(1);
      startVerify.mutate();
    }
  }, [conn.data, attempt, startVerify]);
  useEffect(() => {
    if (attempt === 0 || connVerified) {
      setConnTimedOut(false);
      return;
    }
    setConnTimedOut(false);
    const h = setTimeout(() => setConnTimedOut(true), 45_000);
    return () => clearTimeout(h);
  }, [attempt, connVerified]);
  const recheckConn = () => {
    setConnTimedOut(false);
    setAttempt((n) => n + 1);
    startVerify.mutate();
  };
  const connState: 'ok' | 'failed' | 'pending' = connVerified
    ? 'ok'
    : connTimedOut
      ? 'failed'
      : 'pending';

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-clean-bg text-clean-fg">
          <Check className="h-5 w-5" />
        </span>
        <div className="flex-1">
          <div className="text-[18px] font-bold tracking-[-0.02em] text-text">
            {t('ti.installedTitle')}
          </div>
          <div className="text-[12.5px] text-text-3">
            {t('ti.installedVia', { method: t(`ti.m_${method}`) })}
          </div>
        </div>
      </div>
      {/* Our automatic connection check — the tracker responds. */}
      <div
        className={`mb-2.5 flex items-start gap-2.5 rounded-xl border px-3.5 py-3 text-[12.5px] leading-relaxed ${
          connState === 'ok'
            ? 'border-clean-border bg-clean-bg text-clean-fg'
            : connState === 'failed'
              ? 'border-[#f3dca6] bg-[#fdf6e8] text-[#7c5212]'
              : 'border-[#cfe0ff] bg-accent-bg text-text-2'
        }`}
      >
        {connState === 'ok' ? (
          <Check className="mt-0.5 h-4 w-4 shrink-0" />
        ) : connState === 'failed' ? (
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
        ) : (
          <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" />
        )}
        <div className="flex-1">
          <div className="font-semibold text-text">
            {connState === 'ok'
              ? t('ti.connVerifiedOk')
              : connState === 'failed'
                ? t('ti.connFailed')
                : t('ti.connVerifying')}
          </div>
          <div className="mt-0.5">
            {connState === 'ok'
              ? t('ti.connVerifiedSub')
              : connState === 'failed'
                ? t('ti.connFailedSub')
                : t('ti.connVerifyingSub')}
          </div>
        </div>
        {connState !== 'ok' ? (
          <button
            type="button"
            onClick={recheckConn}
            disabled={startVerify.isPending}
            className="shrink-0 text-[12px] font-semibold text-primary hover:underline disabled:opacity-50"
          >
            {connState === 'failed' ? t('ti.connRetry') : t('ti.recheck')}
          </button>
        ) : null}
      </div>
      {/* The first REAL visitor (a counted beacon) — shown only once it actually
          arrives, as a positive confirmation. Never an endless "waiting" spinner:
          the operational state is already carried by the verified row above. */}
      {active ? (
        <div className="flex items-start gap-2.5 rounded-xl border border-clean-border bg-clean-bg px-3.5 py-3 text-[12.5px] leading-relaxed text-clean-fg">
          <Check className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-semibold text-text">{t('ti.signalActive')}</div>
            <div className="mt-0.5">{t('ti.signalActiveSub')}</div>
          </div>
        </div>
      ) : null}
      <div className="mt-5 flex items-center justify-between">
        <button type="button" onClick={onReconfigure} className={btnGhost}>
          {t('ti.reconfigure')}
        </button>
        {connState === 'ok' || active ? (
          <button type="button" onClick={onDone} className={btnPrimary}>
            {t('ti.done')}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function VerifySignal({
  projectId,
  site,
  bonus,
  onDone,
  onFinishLater,
}: {
  projectId: string;
  site: string;
  bonus?: boolean;
  onDone: () => void;
  /** Onboarding escape: reach the dashboard with the tracker left pending when
   *  the first signal never arrives — shown only in the timeout state. */
  onFinishLater?: () => void;
}): ReactElement {
  const { t } = useTranslation('pages');
  const [phase, setPhase] = useState<'polling' | 'received' | 'detected' | 'timeout'>('polling');
  const [secs, setSecs] = useState(0);
  const [csp, setCsp] = useState<CspCheckResult | null>(null);
  const [cspLoading, setCspLoading] = useState(false);
  const MAX_SECS = 42;

  // Auto-poll the server signal check every 3s, capped (~42s). No manual refresh.
  useEffect(() => {
    if (phase !== 'polling') return;
    let cancelled = false;
    let elapsed = 0;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async (): Promise<void> => {
      try {
        const r = await api.verifyProject(projectId);
        if (cancelled) return;
        if (r.verified) {
          setPhase('received');
          return;
        }
        // Tracker present on the page — verified by a server-side HTML check, with
        // NO beacon and NO counted visit (the anti-fraud analytics stay clean). The
        // install is confirmed automatically, without the client visiting the site.
        if (r.codeDetected) {
          setPhase('detected');
          return;
        }
      } catch {
        /* transient — keep polling */
      }
      if (cancelled) return;
      elapsed += 3;
      setSecs(elapsed);
      if (elapsed >= MAX_SECS) {
        setPhase('timeout');
        return;
      }
      timer = setTimeout(tick, 3000);
    };
    timer = setTimeout(tick, 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [phase, projectId]);

  // Signal received OR tracker detected → hand back to the flow shortly.
  useEffect(() => {
    if (phase !== 'received' && phase !== 'detected') return;
    const tmr = setTimeout(onDone, 1200);
    return () => clearTimeout(tmr);
  }, [phase, onDone]);

  // No signal in time → diagnose the site's CSP (read-only) to explain why.
  useEffect(() => {
    if (phase !== 'timeout') return;
    let cancelled = false;
    setCspLoading(true);
    api
      .cspCheck(projectId)
      .then((r) => !cancelled && setCsp(r))
      .catch(() => !cancelled && setCsp(null))
      .finally(() => !cancelled && setCspLoading(false));
    return () => {
      cancelled = true;
    };
  }, [phase, projectId]);

  const recheck = () => {
    setCsp(null);
    setSecs(0);
    setPhase('polling');
  };

  if (phase === 'received')
    return (
      <div className="py-8 text-center">
        <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-clean-bg text-clean-fg">
          <Check className="h-6 w-6" />
        </span>
        <div className="mt-3 text-[16px] font-bold text-text">{t('ti.installedOk')}</div>
      </div>
    );

  if (phase === 'detected')
    return (
      <div className="py-8 text-center">
        <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-clean-bg text-clean-fg">
          <Check className="h-6 w-6" />
        </span>
        <div className="mt-3 text-[16px] font-bold text-text">{t('ti.detectedOk')}</div>
        <div className="mt-1 text-[13px] text-text-2">{t('ti.detectedOkSub')}</div>
      </div>
    );

  if (phase === 'polling')
    return (
      <div className="py-6 text-center">
        <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
        <div className="mt-3 text-[15px] font-bold text-text">{t('ti.verifying')}</div>
        <div className="mt-1 text-[13px] text-text-2">
          {t('ti.verifyingSub', { site })} · {secs}s
        </div>
        {bonus ? (
          <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-[#f6e2c4] bg-[#fdf4e7] px-3.5 py-3 text-left">
            <Cloud className="mt-0.5 h-5 w-5 shrink-0 text-[#f6821f]" />
            <div className="text-[12.5px] leading-relaxed text-text-2">{t('ti.cfBonus')}</div>
          </div>
        ) : null}
      </div>
    );

  // phase === 'timeout'
  return (
    <div>
      <div className="mb-3 flex items-start gap-2.5 rounded-xl border border-[#f3dca6] bg-[#fdf6e8] px-3.5 py-3 text-[#7c5212]">
        <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0" />
        <div className="text-[13.5px] font-semibold">{t('ti.noSignalTitle')}</div>
      </div>
      {cspLoading ? (
        <div className="flex items-center gap-2 text-[13px] text-text-3">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('ti.cspChecking')}
        </div>
      ) : csp?.blocking ? (
        <CspHelp />
      ) : (
        <p className="text-[13px] leading-relaxed text-text-2">
          {t('ti.noSignalGeneric', { site })}
        </p>
      )}
      <div className="mt-5 flex items-center justify-between gap-3">
        <button type="button" onClick={recheck} className={btnPrimary}>
          {t('ti.recheck')}
        </button>
        <a
          href="/support"
          target="_blank"
          rel="noreferrer"
          className="text-[12px] font-semibold text-primary hover:underline"
        >
          {t('onb.support')}
        </a>
      </div>
      {onFinishLater ? (
        <div className="mt-4 border-t border-border pt-4 text-center">
          <button type="button" onClick={onFinishLater} className={btnGhost}>
            {t('ti.finishLater')}
          </button>
          <p className="mx-auto mt-1.5 max-w-md text-[11.5px] leading-relaxed text-text-3">
            {t('ti.finishLaterSub')}
          </p>
        </div>
      ) : null}
    </div>
  );
}

/** "CSP help" panel: the client's security policy blocks the tracker domain —
 *  show the exact domain to allow + copyable .htaccess / meta / nginx examples.
 *  Speaks only of "CSP" — never of the internal mechanism (Zaraz/Worker). */
function CspHelp(): ReactElement {
  const { t } = useTranslation('pages');
  const D = 'https://track.silospot.com';
  const examples: Array<{ label: string; code: string }> = [
    {
      label: '.htaccess (Apache)',
      code: `Header set Content-Security-Policy "script-src 'self' ${D}; connect-src 'self' ${D}"`,
    },
    {
      label: t('ti.cspMeta'),
      code: `<meta http-equiv="Content-Security-Policy" content="script-src 'self' ${D}; connect-src 'self' ${D}">`,
    },
    {
      label: 'nginx',
      code: `add_header Content-Security-Policy "script-src 'self' ${D}; connect-src 'self' ${D}";`,
    },
  ];
  return (
    <div className="rounded-xl border border-[#cfe0ff] bg-accent-bg px-3.5 py-3.5">
      <div className="text-[14px] font-bold text-text">{t('ti.cspTitle')}</div>
      <p className="mt-1 text-[12.5px] leading-relaxed text-text-2">{t('ti.cspBody')}</p>
      <p className="mt-2.5 text-[12.5px] font-semibold text-text">{t('ti.cspInstruction')}</p>
      <div className="mt-3 flex flex-col gap-2.5">
        {examples.map((ex) => (
          <div key={ex.label}>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.05em] text-text-3">
              {ex.label}
            </div>
            <CodeCopy code={ex.code} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** A read-only code line with a copy-to-clipboard button. */
function CodeCopy({ code }: { code: string }): ReactElement {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard
      ?.writeText(code)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  };
  return (
    <div className="flex items-stretch gap-2">
      <code className="flex-1 overflow-x-auto whitespace-nowrap rounded-lg border border-border bg-surface px-2.5 py-2 font-mono text-[11.5px] text-text-2">
        {code}
      </code>
      <button
        type="button"
        onClick={copy}
        className="grid w-9 shrink-0 place-items-center rounded-lg border border-border text-text-2 hover:bg-bg"
      >
        {copied ? <Check className="h-4 w-4 text-clean-fg" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

function MethodHead({
  icon,
  title,
  sub,
  onBack,
}: {
  icon: ReactElement;
  title: string;
  sub?: string;
  onBack?: () => void;
}): ReactElement {
  return (
    <div className="mb-4 flex items-center gap-3">
      <span className="grid h-10 w-10 place-items-center rounded-xl bg-bg text-primary">
        {icon}
      </span>
      <div className="flex-1">
        <div className="text-[18px] font-bold tracking-[-0.02em] text-text">{title}</div>
        {sub ? <div className="text-[12.5px] text-text-3">{sub}</div> : null}
      </div>
      {onBack ? null : null}
    </div>
  );
}

function MethodError({
  title,
  body,
  onRetry,
  onFallback,
  fallbackLabel,
}: {
  title: string;
  body: string;
  onRetry: () => void;
  onFallback: () => void;
  fallbackLabel: string;
}): ReactElement {
  const { t } = useTranslation('pages');
  return (
    <div>
      <div className="flex items-start gap-2.5 rounded-xl border border-[#f3c9c9] bg-fraud-bg px-3.5 py-3">
        <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-fraud-fg" />
        <div>
          <div className="text-[14px] font-bold text-fraud-fg">{title}</div>
          <p className="mt-1 text-[13px] leading-relaxed text-fraud-fg">{body}</p>
        </div>
      </div>
      <div className="mt-5 flex items-center justify-between">
        <button type="button" onClick={onRetry} className={btnGhost}>
          {t('ti.retry')}
        </button>
        <button type="button" onClick={onFallback} className={btnPrimary}>
          {fallbackLabel}
        </button>
      </div>
    </div>
  );
}

function Reassure({
  children,
  className = '',
}: { children: React.ReactNode; className?: string }): ReactElement {
  return (
    <div
      className={`flex items-start gap-2.5 rounded-xl border border-clean-border bg-clean-bg px-3.5 py-2.5 text-[12.5px] leading-relaxed text-clean-fg ${className}`}
    >
      <Check className="mt-0.5 h-4 w-4 shrink-0" />
      <div>{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <div className="flex items-center justify-between gap-3 text-[13px]">
      <span className="text-text-2">{label}</span>
      <span className="text-right font-medium text-text">{value}</span>
    </div>
  );
}

function BackRow({ onBack }: { onBack: () => void }): ReactElement {
  const { t } = useTranslation('pages');
  return (
    <div className="mt-2">
      <button type="button" onClick={onBack} className={btnGhost}>
        {t('ti.back')}
      </button>
    </div>
  );
}

const btnPrimary =
  'inline-flex items-center gap-2 rounded-[10px] bg-primary px-4 py-2.5 text-[13.5px] font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50';
const btnGhost =
  'inline-flex items-center gap-2 rounded-[10px] px-3 py-2.5 text-[13.5px] font-semibold text-text-2 hover:bg-bg disabled:opacity-40';
const btnDanger =
  'inline-flex items-center gap-2 rounded-[10px] border border-[#f3c9c9] bg-fraud-bg px-4 py-2.5 text-[13.5px] font-semibold text-fraud-fg hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50';
const btnGoogle =
  'inline-flex items-center gap-2 rounded-[10px] border border-border-strong bg-surface px-4 py-2.5 text-[13.5px] font-semibold text-text hover:bg-bg disabled:opacity-50';
const kicker = 'text-[11px] font-semibold uppercase tracking-[0.06em] text-primary';
const h1 = 'mt-1 text-[20px] font-bold tracking-[-0.02em] text-text';
const lead = 'mb-4 mt-1.5 max-w-xl text-[13.5px] leading-relaxed text-text-2';
